import { useFormik } from "formik";
import { find, get, isEmpty } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { FiEdit } from "react-icons/fi";
import {
  MdOutlineCake,
  MdOutlineDiversity1,
  MdOutlineFace,
} from "react-icons/md";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  CURRENCY_SYMBOL,
  DATE_FORMAT,
  RELATIONSHIP,
  TEMPLATE_TYPES,
  TEMPLATES,
} from "utils/constants";
import { costToFrench } from "utils/helpers/values";

import { Col, Row } from "antd";
import { BiIdCard } from "react-icons/bi";
import { RiCalendarEventLine } from "react-icons/ri";
import { bindInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Cost from "../Components/Cost";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Stars from "../Components/Stars";
import DocumentDownload from "./DocumentDownload";
import validation from "./validation";

const NUMBERS_MAPPING = {
  1: "Premier",
  2: "Deuxième",
  3: "Troisième",
  4: "Quatrième",
  5: "Cinquième",
  6: "Sixième",
  7: "Septième",
  8: "Huitième",
  9: "Neuvième",
  10: "Dixième",
  11: "Onzième",
  12: "Douzième",
};

function View({
  subscription,
  onSubmit,
  generalCost,
  updateProjectStatus,
  attachments,
  downloadFile,
  documentsLoading,
  onBack,
}) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const stars =
    parseInt(
      String(get(generalCost, "insurancePlan.alias")).replace(/\D/g, ""),
    ) || 1;

  const EDIT_BLOCK = [
    {
      icon: <MdOutlineFace size={24} />,
      label: "Vous êtes",
      value: `${get(subscription, "project.contact.user.firstname")} ${get(
        subscription,
        "project.contact.user.lastname",
      )}`,
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY].ADHERENT_INFO,
      isBeforeLocked: true,
    },
    {
      icon: <MdOutlineCake size={24} />,
      label: "Date de naissance",
      value: `${moment(
        get(subscription, "project.contact.user.birthDate"),
      ).format(DATE_FORMAT)}`,
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY]
          .ADHERENT_BIRTHDATE,
      isBeforeLocked: true,
    },
    {
      icon: <BiIdCard size={24} />,
      label: "Adresse postale",
      value: (
        <>
          {get(
            find(get(subscription, "project.contact.telecoms"), {
              system: CONTACT_POINT_SYSTEM.ADDRESS,
            }),
            "value",
          )}
          <br />
          {`${get(
            find(get(subscription, "project.contact.telecoms"), {
              system: CONTACT_POINT_SYSTEM.CITY,
            }),
            "value",
          )} - ${get(
            find(get(subscription, "project.contact.telecoms"), {
              system: CONTACT_POINT_SYSTEM.ZIP_CODE,
            }),
            "value",
          )}`}
        </>
      ),
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY]
          .ADHERENT_TELECOMS,
    },
    ...(!isEmpty(get(subscription, "project.contact.relatedPersons", []))
      ? [
          {
            icon: <MdOutlineDiversity1 size={24} />,
            label: "Vos bénéficiaires",
            value: [
              get(subscription, "project.contact.relatedPersons", [])
                .filter(
                  ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
                )
                .map(({ identity }, i) => (
                  <div>
                    {`${get(identity, "user.firstname", "") || ""} ${
                      get(identity, "user.lastname", "") || ""
                    }`}{" "}
                    <span>{"(Votre conjoint)"}</span>
                  </div>
                )),
              get(subscription, "project.contact.relatedPersons", [])
                .filter(
                  ({ relationship }) => relationship === RELATIONSHIP.CHILD,
                )
                .map(({ identity }, i) => (
                  <div>
                    {`${get(identity, "user.firstname", "") || ""} ${
                      get(identity, "user.lastname", "") || ""
                    }`}{" "}
                    <span>{`(Votre ${NUMBERS_MAPPING[i + 1]} enfant)`}</span>
                  </div>
                )),
            ],
            template:
              TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY]
                .RELATED_PERSONS,
          },
        ]
      : []),
  ];

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues: {
      additionalInfo: get(subscription, "additionalInfo"),
    },
    onSubmit: onSubmit,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
  });

  return (
    <Layout>
      <Header
        title="Nous y sommes ! Votre adhésion est prête <b>pour être signée</b>"
        highlight="Nous y sommes !"
        subtitle="Nous y sommes !"
      />

      <div className="recap__wrapper">
        <Row justify={"center"} gutter={[24, 24]}>
          <Col xs={24} sm={20} md={18} lg={12} xl={10}>
            <div className="recap__wrapper--col border">
              <div className="recap__wrapper--blocks">
                <div className="recap__wrapper--title">Offre</div>
                <div className="recap__wrapper--card">
                  <div>
                    <Stars count={stars} />
                    <h3>{get(generalCost, "insurancePlan.name")}</h3>
                  </div>
                  <Cost
                    cost={get(generalCost, "cost", 0)}
                    currency={get(generalCost, "currency")}
                  />
                  <div className="recap__wrapper--card--yearly">
                    {`soit une cotisation de ${costToFrench(
                      get(generalCost, "cost", 0) * 12,
                    )} ${
                      CURRENCY_SYMBOL[get(generalCost, "currency")] || ""
                    } par an`}
                  </div>
                </div>
              </div>
              {get(
                subscription,
                `project.fields.upsells.${get(
                  subscription,
                  "insurancePlan.id",
                )}`,
                [],
              ).length > 0 && (
                <div className="recap__wrapper--blocks">
                  <div className="recap__wrapper--title">
                    Sur-complémentaire
                  </div>
                  {get(
                    subscription,
                    `project.fields.upsells.${get(
                      subscription,
                      "insurancePlan.id",
                    )}`,
                    [],
                  ).map(
                    ({ insurancePlan: { name, alias }, cost, currency }) => {
                      const upsellStars =
                        parseInt(String(alias).replace(/\D/g, "")) || 1;
                      return (
                        <div className="recap__wrapper--upsell-card" key={name}>
                          <div className="recap__wrapper--upsell-card--left">
                            <h3>{name}</h3>
                            <Stars count={upsellStars} />
                          </div>
                          <div className="recap__wrapper--upsell-card--right">
                            <div>
                              <span>
                                {cost} {CURRENCY_SYMBOL[currency] || ""}
                              </span>
                              /mois
                            </div>
                            <div className="recap__wrapper--card--yearly">
                              {`soit une cotisation de ${costToFrench(
                                cost * 12,
                              )} ${CURRENCY_SYMBOL[currency] || ""} par an`}
                            </div>
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
              )}
              <div className="recap__wrapper--blocks">
                <div className="recap__wrapper--title">
                  Documents précontractuels
                </div>
                <DocumentDownload
                  items={attachments}
                  download={(id) => downloadFile(id)}
                />
              </div>
            </div>
          </Col>
          <Col xs={24} sm={20} md={18} lg={12} xl={10}>
            <div className="recap__wrapper--col">
              <div className="recap__wrapper--blocks">
                <div className="recap__wrapper--title">
                  Informations de l’adhérent
                </div>
                {EDIT_BLOCK.map(
                  ({
                    icon,
                    label,
                    value,
                    template,
                    isBeforeLocked = false,
                  }) => (
                    <div className="recap__wrapper--edit" key={label}>
                      {icon}
                      <div className="recap__wrapper--edit--text">
                        <span>{label}</span>
                        <div className="recap__wrapper--edit--text--value">
                          {value}
                        </div>
                      </div>
                      <div className="recap__wrapper--edit--icon">
                        <FiEdit
                          onClick={() =>
                            updateProjectStatus({ template, isBeforeLocked })
                          }
                          size={16}
                        />
                      </div>
                    </div>
                  ),
                )}
              </div>
              <div className="recap__wrapper--blocks">
                <div className="recap__wrapper--title">Date d’effet</div>
                <div className="recap__wrapper--edit">
                  <RiCalendarEventLine size={24} />
                  <div className="recap__wrapper--edit--text">
                    <span>Date d’effet</span>
                    <div className="recap__wrapper--edit--text--value">
                      {moment(
                        get(subscription, "project.contract.issuanceDate"),
                      ).format(DATE_FORMAT)}
                    </div>
                  </div>
                  <div className="recap__wrapper--edit--icon">
                    <FiEdit
                      onClick={() =>
                        updateProjectStatus({
                          template:
                            TEMPLATES.B2C.THEMES[
                              TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY
                            ].EFFECTIVE_DATE,
                        })
                      }
                      size={16}
                    />
                  </div>
                </div>
              </div>
              <hr className="recap__wrapper--divider" />
              <div className="recap__wrapper--blocks">
                <div className="recap__wrapper--title">
                  Adhésion dans le cadre d’un parrainage
                </div>
                <div className="recap__wrapper--edit">
                  <Input
                    placeholder={"123456"}
                    label={"Code parrainage"}
                    defaultValue={get(
                      subscription,
                      "additionalInfo.referralCode",
                    )}
                    {...bindInputProps({
                      name: "additionalInfo.referralCode",
                      values,
                      ...formProps,
                    })}
                  />
                </div>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={20} md={20} lg={20} xl={20}>
            <div className="--consent-wrapper">
              <div className="form-field mb-16">
                <Input
                  type="checkbox"
                  label={
                    "Je confirme avoir reçu le <b class='primary underline'>Règlement Mutualiste</b class='primary underline'>, <b class='primary underline'>statuts</b class='primary underline'>, <b class='primary underline'>notices</b class='primary underline'>, <b class='primary underline'>IPID</b class='primary underline'> et <b class='primary underline'>tableau de garanties</b>."
                  }
                  {...bindInputProps({
                    name: "project.fields.acceptProductStatuses",
                    values,
                    ...formProps,
                  })}
                />
              </div>
              <div className="form-field mb-16">
                <Input
                  type="checkbox"
                  label={
                    "Je suis informé que ses données personnelles sont collectées pour l’exécution du contrat."
                  }
                  {...bindInputProps({
                    name: "project.fields.acceptFees",
                    values,
                    ...formProps,
                  })}
                />
              </div>
              <div className="form-field mb-16">
                <Input
                  type="checkbox"
                  label={
                    "Je déclare avoir pris connaissance des <b class='primary underline'>conditions générales</b> et accepter les <b class='primary underline'>termes du contrat</b>."
                  }
                  {...bindInputProps({
                    name: "project.fields.acceptAgreement",
                    values,
                    ...formProps,
                  })}
                />
              </div>
              <ActionButtons
                onBack={onBack}
                // onNext={() => onSubmit({ origin })}
                // disabled={!origin || documentsLoading}
                onNext={handleSubmit}
                disabled={!isEmpty(formProps.errors)}
                loading={documentsLoading}
                buttonText={"Je signe mon adhésion"}
              />
            </div>
          </Col>
        </Row>
      </div>
    </Layout>
  );
}

export default View;
